import React, { useState, useEffect, useMemo, useRef } from 'react';
import styled from 'styled-components';
import { theme } from '../../../theme';
import DashboardLayout from '../../../components/dashboard/DashboardLayout';
import { Button } from '../../../components/ui/Button';
import { Input } from '../../../components/ui/Input';
import { Modal } from '../../../components/ui/Modal';
import { useAuth } from '../../../contexts/AuthContext';
import toast from 'react-hot-toast';
import {
  FiUser,
  FiEye,
  FiChevronRight,
  FiX,
  FiPlus,
  FiClock,
  FiAlertCircle,
  FiArrowUp,
  FiFile,
  FiRotateCw,
  FiTrash2,
} from 'react-icons/fi';
import { FaDownload, FaChevronLeft } from 'react-icons/fa';
import { clearAllDeployments, clearSingleGuardDeployment } from '../../deployment/service/deploymentService';
import {
  fetchGuards,
  fetchGuardCount,
  loadAllGuardsForExport,
  updateGuardStatus,
  exportToExcelOnly,
  bulkUpdateSalary,
  GuardData,
  PaginationResult,
  DocumentSnapshot,
} from '../service/guardService';
import {
  downloadExcelTemplate,
  uploadExcelFile,
} from '../create_guard/service/createGuardService';
import GuardModal from './GuardModal';
import StatusModal from './StatusModal';
import CreateGuardModal from '../create_guard/view/CreateGuardModal';
import GuardDocumentsModal from './GuardDocumentsModal';
import DeploymentHistoryModal from './DeploymentHistoryModal';

const PageContainer = styled.div`
  width: 100%;
  max-width: 100%;
  box-sizing: border-box;
  overflow: hidden;
  display: flex;
  flex-direction: column;
  flex: 1;
  min-height: 0;

  @media (max-width: ${theme.breakpoints.md}) {
    overflow: visible;
  }
`;

const SearchAndActionsContainer = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: ${theme.spacing[4]};
  margin-bottom: ${theme.spacing[6]};
  flex-wrap: wrap;
  padding: ${theme.spacing[4]};
  background: ${theme.colors.white};
  border-radius: ${theme.borderRadius.lg};
  box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.05);

  @media (max-width: ${theme.breakpoints.md}) {
    flex-direction: column;
    align-items: stretch;
    padding: ${theme.spacing[3]};
    margin: 0 0 ${theme.spacing[2]} 0;
    gap: ${theme.spacing[3]};
    border-bottom: 1px solid ${theme.colors.light.border.light};
    box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.05);
  }
`;

const ScrollToTopButton = styled.button<{ show: boolean }>`
  position: fixed;
  bottom: 20px;
  right: 20px;
  width: 44px;
  height: 44px;
  border-radius: 50%;
  background-color: ${theme.colors.primary[600]};
  color: ${theme.colors.white};
  border: none;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  box-shadow: 0 4px 12px rgba(124, 58, 237, 0.3);
  z-index: 100;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  opacity: ${({ show }) => (show ? '1' : '0')};
  visibility: ${({ show }) => (show ? 'visible' : 'hidden')};
  transform: ${({ show }) => (show ? 'translateY(0)' : 'translateY(20px)')};

  &:hover {
    background-color: ${theme.colors.primary[700]};
    transform: translateY(-5px);
    box-shadow: 0 6px 16px rgba(124, 58, 237, 0.4);
  }

  &:active {
    transform: scale(0.95);
  }
`;

const SearchWrapper = styled.div`
  flex: 1;
  max-width: 400px;
  min-width: 250px;
  
  @media (max-width: ${theme.breakpoints.md}) {
    max-width: 100%;
    width: 100%;
  }
`;

const StatsContainer = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(150px, 1fr));
  gap: ${theme.spacing[2]};
  margin-bottom: ${theme.spacing[3]};
  padding: ${theme.spacing[2]} 0;

  @media (max-width: ${theme.breakpoints.md}) {
    grid-template-columns: repeat(2, 1fr);
    margin: 0 ${theme.spacing[3]} ${theme.spacing[2]};
    gap: ${theme.spacing[2]};
  }
`;

const StatCard = styled.div`
  background: ${theme.colors.white};
  padding: ${theme.spacing[2]} ${theme.spacing[3]};
  border-radius: ${theme.borderRadius.md};
  border: 1px solid ${theme.colors.light.border.light};
  text-align: center;
  display: flex;
  flex-direction: column;
  justify-content: center;
  height: 65px;
  transition: all 0.2s ease;

  @media (max-width: ${theme.breakpoints.md}) {
    padding: ${theme.spacing[2]};
    height: 60px;
  }

  &:hover {
    border-color: ${theme.colors.primary[200]};
    box-shadow: 0 2px 4px rgba(0, 0, 0, 0.05);
    transform: translateY(-1px);
  }
`;

const StatValue = styled.div<{ color?: string }>`
  font-size: ${theme.typography.fontSize.lg};
  font-weight: ${theme.typography.fontWeight.bold};
  color: ${({ color }) => color || theme.colors.primary[600]};
  margin-bottom: 2px;
  line-height: 1.2;
`;

const StatLabel = styled.div`
  font-size: ${theme.typography.fontSize.xs};
  color: ${theme.colors.light.text.secondary};
  line-height: 1.2;
`;

const ActionButtons = styled.div`
  display: flex;
  gap: ${theme.spacing[3]};
  margin-bottom: ${theme.spacing[4]};
  flex-wrap: wrap;
  padding: ${theme.spacing[3]};
  background: ${theme.colors.light.background.secondary};
  border-radius: ${theme.borderRadius.lg};
  border: 1px solid ${theme.colors.light.border.light};

  @media (max-width: ${theme.breakpoints.md}) {
    padding: ${theme.spacing[3]};
    margin: 0 ${theme.spacing[3]} ${theme.spacing[4]};
    gap: ${theme.spacing[2]};
  }
`;

const MobileCardContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${theme.spacing[3]};
  padding: ${theme.spacing[3]};
  padding-bottom: 80px; /* Space for scroll to top */
`;

const MobileCard = styled.div`
  background: ${theme.colors.white};
  border-radius: ${theme.borderRadius.lg};
  padding: ${theme.spacing[3]};
  border: 1px solid ${theme.colors.light.border.light};
  display: flex;
  align-items: center;
  gap: ${theme.spacing[3]};
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.02);

  &:active {
    background-color: ${theme.colors.light.background.secondary};
  }
`;

const MobileCardImage = styled.img`
  width: 48px;
  height: 48px;
  border-radius: 50%;
  object-fit: cover;
  flex-shrink: 0;
  border: 2px solid ${theme.colors.primary[100]};
`;

const MobileCardContent = styled.div`
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 2px;
`;

const MobileCardName = styled.div`
  font-weight: ${theme.typography.fontWeight.bold};
  color: ${theme.colors.light.text.primary};
  font-size: ${theme.typography.fontSize.sm};
  white-space: normal;
`;

const MobileCardId = styled.div`
  font-size: ${theme.typography.fontSize.xs};
  color: ${theme.colors.light.text.secondary};
`;

const MobileCardStatus = styled.div`
  flex-shrink: 0;
`;

const TableWrapper = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  min-height: 0;
  background-color: ${theme.colors.white};
  border-radius: ${theme.borderRadius.lg};
  border: 1px solid ${theme.colors.light.border.light};
  margin: 0 ${theme.spacing[4]} ${theme.spacing[4]};
  overflow: hidden;

  @media (max-width: ${theme.breakpoints.md}) {
    margin: 0;
    border-radius: 0;
    border: none;
  }
`;

const TableBodyWrapper = styled.div<{ showScrollbar?: boolean }>`
  flex: 1;
  min-height: 0;
  overflow: auto;
  position: relative;
  -webkit-overflow-scrolling: touch;
  
  scrollbar-width: thin;
  scrollbar-color: ${theme.colors.primary[200]} transparent;

  &::-webkit-scrollbar {
    width: 6px;
    height: 6px;
  }

  &::-webkit-scrollbar-track {
    background: transparent;
  }

  &::-webkit-scrollbar-thumb {
    background-color: ${theme.colors.primary[200]};
    border-radius: 20px;
    opacity: 0;
    transition: opacity 0.2s ease;
  }

  &:hover::-webkit-scrollbar-thumb {
    opacity: 0.5;
  }
  
  &.scrolling::-webkit-scrollbar-thumb {
    opacity: 0.8;
    transition: opacity 0.2s ease 0s;
  }
  
  &::-webkit-scrollbar-corner {
    background: transparent;
  }
`;

const StyledTable = styled.table`
  width: 100%;
  border-collapse: separate;
  border-spacing: 0;
  min-width: 1200px;
  background-color: ${theme.colors.white};
  
  @media (max-width: 1200px) {
    width: 1200px;
    min-width: 1200px;
  }
  
  thead {
    position: sticky;
    top: 0;
    z-index: 10;
    background: linear-gradient(135deg, ${theme.colors.primary[600]} 0%, ${theme.colors.primary[700]} 100%);
  }

  th {
    padding: ${theme.spacing[4]} ${theme.spacing[6]};
    text-align: left;
    font-size: ${theme.typography.fontSize.xs};
    font-weight: ${theme.typography.fontWeight.bold};
    color: ${theme.colors.white};
    text-transform: uppercase;
    letter-spacing: 0.05em;
    white-space: nowrap;
    position: relative;

    &:not(:last-child)::after {
      content: '';
      position: absolute;
      right: 0;
      top: 25%;
      height: 50%;
      width: 1px;
      background: linear-gradient(180deg, transparent, ${theme.colors.primary[300]}, transparent);
    }
  }
  
  td {
    overflow: hidden;
    text-overflow: ellipsis;
    padding: ${theme.spacing[5]} ${theme.spacing[6]};
    font-size: ${theme.typography.fontSize.sm};
    color: ${theme.colors.light.text.primary};
    vertical-align: middle;
    border-bottom: 1px solid ${theme.colors.light.border.light};
  }

  /* Specific column widths for better layout */
  th:nth-child(1), td:nth-child(1) { width: 22%; } /* Guard Info */
  th:nth-child(2), td:nth-child(2) { width: 15%; } /* Mobile */
  th:nth-child(3), td:nth-child(3) { width: 12%; } /* Salary */
  th:nth-child(4), td:nth-child(4) { width: 20%; } /* Current Deployment */
  th:nth-child(5), td:nth-child(5) { width: 15%; } /* Status */
  th:nth-child(6), td:nth-child(6) { width: 13%; } /* Actions */
  
  tbody tr {
    transition: background-color 0.2s ease;
    
    &:hover {
      background-color: ${theme.colors.primary[50]} !important;
    }
    
    &:nth-child(even) {
      background-color: ${theme.colors.white};
    }
    
    &:nth-child(odd) {
      background-color: ${theme.colors.light.background.secondary};
    }
  }
  
  tfoot {
    background: linear-gradient(135deg, ${theme.colors.primary[50]} 0%, ${theme.colors.primary[100]} 100%);
    border-top: 2px solid ${theme.colors.primary[200]};
    flex-shrink: 0;
    
    tr:last-child td:first-child {
      border-bottom-left-radius: ${theme.borderRadius.lg};
    }
    
    tr:last-child td:last-child {
      border-bottom-right-radius: ${theme.borderRadius.lg};
    }
    
    td {
      padding: ${theme.spacing[2]} ${theme.spacing[3]};
      text-align: center;
      position: relative;
    }
  }
`;

const PaginationWrapper = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  gap: ${theme.spacing[1]};
  width: 100%;
`;

const PaginationNavButton = styled.button<{ isNext?: boolean }>`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  border: none;
  background-color: ${theme.colors.white};
  color: ${theme.colors.primary[600]};
  border-radius: ${theme.borderRadius.md};
  cursor: pointer;
  transition: all 0.2s ease;
  
  &:disabled {
    color: ${theme.colors.light.text.muted};
    cursor: not-allowed;
    background-color: transparent;
  }
  
  &:not(:disabled):hover {
    background-color: ${theme.colors.primary[100]};
    transform: ${({ isNext }) => isNext ? 'translateX(2px)' : 'translateX(-2px)'};
  }
`;

const PageNumberButton = styled.button<{ active?: boolean }>`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 32px;
  height: 32px;
  padding: 0 ${theme.spacing[2]};
  font-size: ${theme.typography.fontSize.sm};
  font-weight: ${theme.typography.fontWeight.medium};
  color: ${({ active }) => active ? theme.colors.white : theme.colors.primary[600]};
  background-color: ${({ active }) => active ? theme.colors.primary[600] : theme.colors.white};
  border: none;
  border-radius: ${theme.borderRadius.md};
  cursor: pointer;
  transition: all 0.2s ease;
  
  &:hover {
    background-color: ${({ active }) => active ? theme.colors.primary[700] : theme.colors.primary[100]};
    transform: translateY(-1px);
  }
`;

const Ellipsis = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 32px;
  color: ${theme.colors.light.text.secondary};
`;

const Badge = styled.span<{ variant?: 'active' | 'inactive' }>`
  padding: ${theme.spacing[1]} ${theme.spacing[2]};
  border-radius: ${theme.borderRadius.full};
  font-size: ${theme.typography.fontSize.xs};
  font-weight: ${theme.typography.fontWeight.medium};
  background-color: ${({ variant = 'active' }) =>
    variant === 'active' ? `${theme.colors.accent.success}20` : `${theme.colors.accent.danger}20`};
  color: ${({ variant = 'active' }) =>
    variant === 'active' ? theme.colors.accent.success : theme.colors.accent.danger};
`;

const SwitchContainer = styled.label`
  display: flex;
  align-items: center;
  gap: ${theme.spacing[2]};
  cursor: pointer;
`;

const Switch = styled.div<{ checked: boolean }>`
  width: 40px;
  height: 24px;
  background-color: ${({ checked }) => (checked ? theme.colors.primary[500] : theme.colors.light.text.muted)};
  border-radius: 12px;
  position: relative;
  transition: 0.3s;

  input {
    opacity: 0;
    width: 0;
    height: 0;
  }

  .slider {
    position: absolute;
    cursor: pointer;
    top: 3px;
    left: ${({ checked }) => (checked ? '19px' : '3px')};
    width: 18px;
    height: 18px;
    background-color: white;
    border-radius: 50%;
    transition: 0.3s;
  }
`;

const ActionButton = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: ${theme.spacing[2]};
  border: none;
  background-color: transparent;
  color: ${theme.colors.light.text.secondary};
  cursor: pointer;
  border-radius: ${theme.borderRadius.md};
  transition: all 0.2s ease;

  &:hover {
    background-color: ${theme.colors.primary[500]}10;
    color: ${theme.colors.primary[600]};
    transform: scale(1.1);
  }
`;

const DetailRow = styled.div`
  display: flex;
  justify-content: space-between;
  padding: ${theme.spacing[2]} 0;
  border-bottom: 1px dashed ${theme.colors.light.border.light};

  &:last-child {
    border-bottom: none;
  }
`;

const DetailLabel = styled.span`
  font-size: ${theme.typography.fontSize.xs};
  font-weight: ${theme.typography.fontWeight.medium};
  color: ${theme.colors.light.text.secondary};
`;

const DetailValue = styled.span`
  color: ${theme.colors.light.text.primary};
`;

const LoadingContainer = styled.div`
  text-align: center;
  padding: ${theme.spacing[12]} ${theme.spacing[6]};
`;

const Spinner = styled.div`
  width: 40px;
  height: 40px;
  border: 4px solid ${theme.colors.light.border.light};
  border-top: 4px solid ${theme.colors.primary[500]};
  border-radius: 50%;
  animation: spin 1s linear infinite;
  margin: 0 auto ${theme.spacing[4]};

  @keyframes spin {
    0% { transform: rotate(0deg); }
    100% { transform: rotate(360deg); }
  }
`;

const EmptyState = styled.div`
  text-align: center;
  padding: ${theme.spacing[12]} ${theme.spacing[6]};
  color: ${theme.colors.light.text.secondary};
`;

const EmptyStateIcon = styled.div`
  font-size: 48px;
  margin-bottom: ${theme.spacing[4]};
  opacity: 0.5;
`;

const EmptyStateText = styled.p`
  font-size: ${theme.typography.fontSize.lg};
  margin: 0;
`;

const ManageGuardPage: React.FC = () => {
  const { user, companyData, staffData } = useAuth();
  const [guards, setGuards] = useState<GuardData[]>([]);
  const [totalCount, setTotalCount] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [loading, setLoading] = useState(false);
  const [hasNextPage, setHasNextPage] = useState(false);
  const [hasPrevPage, setHasPrevPage] = useState(false);
  const [lastDoc, setLastDoc] = useState<DocumentSnapshot | null>(null);
  const [firstDoc, setFirstDoc] = useState<DocumentSnapshot | null>(null);
  const [pageStack, setPageStack] = useState<DocumentSnapshot[]>([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [allGuards, setAllGuards] = useState<GuardData[]>([]);
  const [selectedGuard, setSelectedGuard] = useState<GuardData | null>(null);
  const [showModal, setShowModal] = useState(false);
  const [showStatusModal, setShowStatusModal] = useState(false);
  const [showScrollTop, setShowScrollTop] = useState(false);
  const [statusGuard, setStatusGuard] = useState<GuardData | null>(null);
  const [updatingStatus, setUpdatingStatus] = useState<string | null>(null);
  const [exportingData, setExportingData] = useState(false);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showHistoryModal, setShowHistoryModal] = useState(false);
  const [historyGuard, setHistoryGuard] = useState<GuardData | null>(null);
  const [showBulkSalaryModal, setShowBulkSalaryModal] = useState(false);
  const [bulkSalary, setBulkSalary] = useState('');
  const [updatingBulkSalary, setUpdatingBulkSalary] = useState(false);
  const [showScrollbar, setShowScrollbar] = useState(false);
  const scrollTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const tableBodyRef = useRef<HTMLDivElement>(null);
  const [showDocumentModal, setShowDocumentModal] = useState(false);
  const [documentGuard, setDocumentGuard] = useState<GuardData | null>(null);
  const [uploadingExcel, setUploadingExcel] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const loadMoreRef = useRef<HTMLDivElement>(null);

  // Clear Deployments state
  const [showClearConfirmModal, setShowClearConfirmModal] = useState(false);
  const [clearConfirmText, setClearConfirmText] = useState('');
  const [isClearingDeployments, setIsClearingDeployments] = useState(false);

  // Single Guard Reset state
  const [showResetConfirmModal, setShowResetConfirmModal] = useState(false);
  const [resetGuard, setResetGuard] = useState<GuardData | null>(null);
  const [isResettingDeployment, setIsResettingDeployment] = useState(false);
  const [resetConfirmInput, setResetConfirmInput] = useState('');

  useEffect(() => {
    const handleScroll = () => {
      setShowScrollTop(window.scrollY > 300);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const checkDocumentStatus = (guard: GuardData): 'expired' | 'nearExpiry' | 'missing' | 'ok' => {
    const documents = guard.documents;
    if (!documents) return 'missing';
    if (Array.isArray(documents)) {
      const now = new Date();
      const hasExpired = documents.some(doc => doc.expiryDate && new Date(doc.expiryDate) < now);
      return hasExpired ? 'expired' : 'ok';
    }
    return 'ok';
  };

  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const pageSize = isMobile ? 15 : 20;
  const companyId = companyData?.id || user?.uid || '';

  useEffect(() => {
    if (user) {
      loadGuards('first');
      loadCount();
    }
  }, [user, companyData]);

  const loadGuards = async (direction: 'first' | 'next' | 'prev' = 'first') => {
    setLoading(true);
    try {
      const allData = await loadAllGuardsForExport(companyId);
      setAllGuards(allData);
      const result: PaginationResult = await fetchGuards(direction, lastDoc, pageStack, pageSize, companyId);
      if (direction === 'next' && isMobile) {
        setGuards(prev => [...prev, ...result.guards]);
      } else {
        setGuards(result.guards);
      }
      setHasNextPage(result.hasNextPage);
      setHasPrevPage(result.hasPrevPage);
      setFirstDoc(result.firstDoc);
      setLastDoc(result.lastDoc);
      if (direction === 'next') {
        setPageStack((prev) => [...prev, firstDoc!]);
        setCurrentPage((prev) => prev + 1);
      } else if (direction === 'prev') {
        setPageStack((prev) => prev.slice(0, -1));
        setCurrentPage((prev) => prev - 1);
      } else {
        setCurrentPage(1);
        setPageStack([]);
      }
    } catch (error: any) {
      console.error('Error loading guards:', error);
      toast.error('Failed to load guards');
    } finally {
      setLoading(false);
    }
  };

  const loadCount = async () => {
    const count = await fetchGuardCount(companyId);
    setTotalCount(count);
  };

  const filteredGuards = useMemo(() => {
    if (!searchTerm.trim()) return guards;
    const term = searchTerm.toLowerCase();
    return allGuards.filter((g) => {
      const name = g.name?.toLowerCase() || '';
      const gid = g.guardId?.toLowerCase() || '';
      const cnic = g.cnic?.toLowerCase() || '';
      return name.includes(term) || gid.includes(term) || cnic.includes(term);
    });
  }, [searchTerm, allGuards, guards]);

  const paginatedGuards = useMemo(() => {
    if (!searchTerm.trim()) return guards;
    const start = (currentPage - 1) * pageSize;
    return filteredGuards.slice(start, start + pageSize);
  }, [filteredGuards, currentPage, pageSize, searchTerm, guards]);

  const totalPages = useMemo(() => {
    if (searchTerm) return Math.ceil(filteredGuards.length / pageSize) || 1;
    return Math.ceil(totalCount / pageSize) || 1;
  }, [searchTerm, filteredGuards.length, totalCount, pageSize]);

  const getPageNumbers = () => {
    const pages: (number | string)[] = [];
    const maxVisible = 5;
    if (totalPages <= maxVisible) {
      for (let i = 1; i <= totalPages; i++) pages.push(i);
    } else {
      pages.push(1);
      if (currentPage > 3) pages.push('ellipsis-start');
      const start = Math.max(2, currentPage - 1);
      const end = Math.min(totalPages - 1, currentPage + 1);
      for (let i = start; i <= end; i++) {
        if (i !== 1 && i !== totalPages) pages.push(i);
      }
      if (currentPage < totalPages - 2) pages.push('ellipsis-end');
      if (totalPages > 1) pages.push(totalPages);
    }
    return pages;
  };

  const handleReset = () => { setSearchTerm(''); loadGuards('first'); };
  const handleNextPage = () => { if (searchTerm) setCurrentPage(p => p + 1); else loadGuards('next'); };
  const handlePrevPage = () => { if (searchTerm) setCurrentPage(p => p - 1); else loadGuards('prev'); };
  const handleFirstPage = () => { if (searchTerm) setCurrentPage(1); else loadGuards('first'); };

  const handleViewGuard = (guard: GuardData) => { setSelectedGuard(guard); setShowModal(true); };
  const handleStatusChange = (guard: GuardData, newStatus: boolean) => {
    if (!newStatus) { setStatusGuard(guard); setShowStatusModal(true); }
    else handleUpdateStatus(guard, true, '', '');
  };

  const handleUpdateStatus = async (guard: GuardData, isActive: boolean, reason: string, inactiveDate: string) => {
    if (!guard.id) return;
    setUpdatingStatus(guard.id);
    try {
      await updateGuardStatus(guard.id, isActive, reason, inactiveDate, companyId);
      setGuards(prev => prev.map(g => g.id === guard.id ? { ...g, isActive, inactiveReason: isActive ? '' : reason, inactiveDate: isActive ? '' : inactiveDate } : g));
      toast.success(`Guard ${isActive ? 'activated' : 'deactivated'} successfully`);
      setShowStatusModal(false);
      setStatusGuard(null);
    } catch (error) { toast.error('Failed to update guard status'); }
    finally { setUpdatingStatus(null); }
  };

  const handleExportExcel = async () => {
    setExportingData(true);
    try {
      toast.loading('Exporting to Excel...', { id: 'export' });
      const fileName = await exportToExcelOnly(companyId);
      toast.success(`Exported successfully: ${fileName}`, { id: 'export' });
    } catch (error) { toast.error('Failed to export data', { id: 'export' }); }
    finally { setExportingData(false); }
  };

  const handleViewHistory = (guard: GuardData) => { setHistoryGuard(guard); setShowHistoryModal(true); };
  const handleViewDocuments = (guard: GuardData) => { setDocumentGuard(guard); setShowDocumentModal(true); };
  const handleDownloadTemplate = () => {
    try { downloadExcelTemplate(); toast.success('Template downloaded successfully'); }
    catch (error) { toast.error('Failed to download template'); }
  };

  const handleExcelUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file || !companyData?.companyName) return;
    setUploadingExcel(true);
    try {
      const addedCount = await uploadExcelFile(file, companyData.companyName);
      toast.success(`Successfully uploaded ${addedCount} guards`);
      window.location.reload();
    } catch (error) { toast.error('Failed to upload Excel file'); }
    finally { setUploadingExcel(false); }
  };

  const handleClearAllDeployments = async () => {
    if (clearConfirmText !== 'RESET') { toast.error('Please type RESET to confirm'); return; }
    setIsClearingDeployments(true);
    try {
      toast.loading('Resetting all deployments...', { id: 'clear-deployments' });
      const results = await clearAllDeployments(companyId);
      const totalClients = (results.individualClients || 0) + (results.factoryClients || 0) + (results.bankBranches || 0);
      toast.success(`Cleared deployments for ${results.guards} guards and ${totalClients} clients`, { id: 'clear-deployments' });
      setShowClearConfirmModal(false);
      setClearConfirmText('');
      window.location.reload();
    } catch (error: any) { toast.error(error.message || 'Failed to clear deployments', { id: 'clear-deployments' }); }
    finally { setIsClearingDeployments(false); }
  };

  const handleClearGuardDeployment = async () => {
    if (!resetGuard || resetConfirmInput !== 'RESET') {
      toast.error('Please type RESET to confirm');
      return;
    }

    setIsResettingDeployment(true);
    try {
      toast.loading(`Resetting deployment for ${resetGuard.name}...`, { id: 'reset-guard' });
      await clearSingleGuardDeployment(resetGuard.id!, companyId);
      toast.success(`Deployment cleared for ${resetGuard.name}`, { id: 'reset-guard' });
      setShowResetConfirmModal(false);
      setResetConfirmInput('');
      setResetGuard(null);
      loadGuards('first');
    } catch (error: any) {
      toast.error(error.message || 'Failed to reset deployment', { id: 'reset-guard' });
    } finally {
      setIsResettingDeployment(false);
    }
  };

  const userEmail = user?.email?.toLowerCase();
  const isCompanyOwner = !!companyData && (
    !staffData ||
    (userEmail && (companyData.email?.toLowerCase() === userEmail || companyData.emails?.some((e: string) => e.toLowerCase() === userEmail)))
  );

  const canViewGuards = isCompanyOwner || (staffData?.access?.guards === true);
  const canEditGuards = isCompanyOwner || (staffData?.access?.guards === true && (staffData?.permissions?.guards === 'edit' || staffData?.permissions?.guards === 'add' || staffData?.permissions?.guards === 'delete'));
  const canCreateGuards = canEditGuards;

  if (!canViewGuards) {
    return (
      <DashboardLayout>
        <PageContainer>
          <EmptyState>
            <EmptyStateIcon>🚫</EmptyStateIcon>
            <EmptyStateText>You don't have permission to view guards.</EmptyStateText>
          </EmptyState>
        </PageContainer>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <PageContainer>
        <SearchAndActionsContainer>
          <SearchWrapper>
            <Input
              placeholder="Search by name, guard ID, or CNIC..."
              value={searchTerm}
              onChange={(e) => { setSearchTerm(e.target.value); setCurrentPage(1); }}
              dark
            />
          </SearchWrapper>
          <div style={{ display: 'flex', alignItems: 'center', gap: theme.spacing[3] }}>
            {searchTerm && (
              <Button variant="secondary" onClick={handleReset} disabled={loading}>
                <FiX size={18} /> Clear
              </Button>
            )}
            {isCompanyOwner && (
              <Button
                variant="success"
                onClick={handleExportExcel}
                disabled={totalCount === 0 || exportingData || loading}
              >
                <FaDownload size={18} /> Export Excel
              </Button>
            )}
            <input type="file" ref={fileInputRef} accept=".xlsx,.xls" onChange={handleExcelUpload} style={{ display: 'none' }} />
            {canCreateGuards && (
              <>
                <Button variant="secondary" onClick={handleDownloadTemplate} disabled={uploadingExcel} dark>
                  <FaDownload style={{ marginRight: theme.spacing[2] }} /> Template
                </Button>
                <Button variant="secondary" onClick={() => fileInputRef.current?.click()} disabled={uploadingExcel} loading={uploadingExcel} dark>
                  <FiFile style={{ marginRight: theme.spacing[2] }} /> Import Excel
                </Button>
                <Button onClick={() => setShowCreateModal(true)}>
                  <FiPlus size={18} /> Create Guard
                </Button>
                {isCompanyOwner && (
                  <Button variant="danger" onClick={() => setShowClearConfirmModal(true)} title="Reset All Deployments">
                    <FiRotateCw size={18} /> Reset Deployments
                  </Button>
                )}
              </>
            )}
          </div>
        </SearchAndActionsContainer>

        <StatsContainer>
          <StatCard><StatValue>{totalCount}</StatValue><StatLabel>Total Guards</StatLabel></StatCard>
          <StatCard><StatValue color={theme.colors.accent.success}>{allGuards.filter(g => g.isActive !== false).length}</StatValue><StatLabel>Active Guards</StatLabel></StatCard>
          <StatCard><StatValue color={theme.colors.accent.danger}>{allGuards.filter(g => g.isActive === false).length}</StatValue><StatLabel>Inactive Guards</StatLabel></StatCard>
          <StatCard><StatValue color={theme.colors.primary[600]}>{allGuards.filter(g => g.currentDeployment).length}</StatValue><StatLabel>Deployed Guards</StatLabel></StatCard>
        </StatsContainer>

        <Modal isOpen={showClearConfirmModal} onClose={() => !isClearingDeployments && setShowClearConfirmModal(false)} title="Reset All Deployments">
          <div style={{ padding: theme.spacing[4] }}>
            <div style={{ backgroundColor: theme.colors.accent.danger + '10', padding: theme.spacing[4], borderRadius: theme.borderRadius.md, marginBottom: theme.spacing[4], color: theme.colors.accent.danger, display: 'flex', gap: theme.spacing[3], alignItems: 'center' }}>
              <FiAlertCircle size={24} />
              <div>
                <strong>DANGER: Irreversible Action</strong>
                <p style={{ margin: 0, fontSize: theme.typography.fontSize.sm }}>This will remove all current deployments and entire deployment histories for all guards and clients.</p>
              </div>
            </div>
            <p style={{ marginBottom: theme.spacing[2], fontWeight: theme.typography.fontWeight.medium }}>To confirm, please type <strong>RESET</strong> below:</p>
            <Input value={clearConfirmText} onChange={(e) => setClearConfirmText(e.target.value)} placeholder="Type RESET here" dark />
            <div style={{ display: 'flex', gap: theme.spacing[3], marginTop: theme.spacing[6] }}>
              <Button variant="secondary" onClick={() => setShowClearConfirmModal(false)} disabled={isClearingDeployments} fullWidth dark>Cancel</Button>
              <Button variant="danger" onClick={handleClearAllDeployments} disabled={clearConfirmText !== 'RESET' || isClearingDeployments} loading={isClearingDeployments} fullWidth>Reset All Now</Button>
            </div>
          </div>
        </Modal>

        {loading && (searchTerm ? paginatedGuards : guards).length === 0 ? (
          <LoadingContainer><Spinner /><p style={{ color: theme.colors.light.text.secondary }}>Loading guards...</p></LoadingContainer>
        ) : (searchTerm ? paginatedGuards : guards).length > 0 ? (
          <>
            {isMobile ? (
              <MobileCardContainer>
                {(searchTerm ? paginatedGuards : guards).map((guard) => (
                  <MobileCard key={`mobile-card-${guard.id}`} onClick={() => handleViewGuard(guard)}>
                    <MobileCardImage src={guard.image || `https://ui-avatars.com/api/?name=${encodeURIComponent(guard.name || '')}&background=random`} alt={guard.name} />
                    <MobileCardContent>
                      <MobileCardName>{guard.name || 'Unknown Guard'}</MobileCardName>
                      <MobileCardId>ID: {guard.guardId || 'N/A'}</MobileCardId>
                    </MobileCardContent>
                    <MobileCardStatus><Badge variant={guard.isActive !== false ? 'active' : 'inactive'}>{guard.isActive !== false ? 'Active' : 'Inactive'}</Badge></MobileCardStatus>
                  </MobileCard>
                ))}
              </MobileCardContainer>
            ) : (
              <TableWrapper>
                <StyledTable>
                  <thead><tr><th>Guard Info</th><th>Mobile</th><th>Salary</th><th>Current Deployment</th><th>Status</th><th>Actions</th></tr></thead>
                </StyledTable>
                <TableBodyWrapper ref={tableBodyRef}>
                  <StyledTable>
                    <tbody>
                      {(searchTerm ? paginatedGuards : guards).map((guard) => (
                        <tr key={guard.id}>
                          <td>
                            <div style={{ display: 'flex', alignItems: 'center', gap: theme.spacing[2] }}>
                              {guard.image ? <img src={guard.image} alt={guard.name} style={{ width: 40, height: 40, objectFit: 'cover', borderRadius: '50%' }} /> : <FiUser size={24} style={{ color: theme.colors.light.text.muted }} />}
                              <div>
                                <div style={{ fontWeight: theme.typography.fontWeight.medium }}>{guard.name || '—'}</div>
                                <div style={{ fontSize: theme.typography.fontSize.xs, color: theme.colors.light.text.secondary }}>{guard.guardId || '—'} {guard.cnic ? `• ${guard.cnic}` : ''}</div>
                              </div>
                            </div>
                          </td>
                          <td>{guard.contactNo1 || '—'}</td>
                          <td>{guard.salary ? `Rs. ${guard.salary.toLocaleString()}` : '—'}</td>
                          <td>{guard.currentDeployment?.client?.name || '—'}</td>
                          <td><Badge variant={guard.isActive !== false ? 'active' : 'inactive'}>{guard.isActive !== false ? 'Active' : 'Inactive'}</Badge></td>
                          <td>
                            <div style={{ display: 'flex', gap: theme.spacing[2], justifyContent: 'center' }}>
                              <ActionButton onClick={() => handleViewGuard(guard)} title="View Details"><FiEye size={18} /></ActionButton>
                              {((guard.history?.length ?? 0) > 0 || guard.currentDeployment) && <ActionButton onClick={() => handleViewHistory(guard)} title="View History"><FiClock size={18} /></ActionButton>}
                              <ActionButton onClick={() => handleViewDocuments(guard)} title="Manage Documents"><FiFile size={18} /></ActionButton>
                              {isCompanyOwner && (
                                <ActionButton
                                  onClick={() => {
                                    setResetGuard(guard);
                                    setShowResetConfirmModal(true);
                                  }}
                                  title="Reset Deployment"
                                  style={{ color: theme.colors.accent.danger }}
                                >
                                  <FiRotateCw size={18} />
                                </ActionButton>
                              )}
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </StyledTable>
                </TableBodyWrapper>
                <StyledTable>
                  <tfoot>
                    <tr>
                      <td colSpan={6}>
                        <PaginationWrapper>
                          <PaginationNavButton onClick={handlePrevPage} disabled={!hasPrevPage || loading}><FaChevronLeft size={16} /></PaginationNavButton>
                          {getPageNumbers().map((page, i) => (
                            <PageNumberButton key={i} active={currentPage === page} onClick={() => typeof page === 'number' && setCurrentPage(page)}>{page === 'ellipsis-start' || page === 'ellipsis-end' ? '...' : page}</PageNumberButton>
                          ))}
                          <PaginationNavButton onClick={handleNextPage} disabled={!hasNextPage || loading} isNext><FiChevronRight size={16} /></PaginationNavButton>
                        </PaginationWrapper>
                      </td>
                    </tr>
                  </tfoot>
                </StyledTable>
              </TableWrapper>
            )}
          </>
        ) : (
          <EmptyState><EmptyStateIcon>👤</EmptyStateIcon><EmptyStateText>No guards found.</EmptyStateText></EmptyState>
        )}
      </PageContainer>
      {selectedGuard && <GuardModal guard={selectedGuard} isOpen={showModal} onClose={() => setShowModal(false)} onUpdate={() => { loadGuards('first'); loadCount(); }} canEdit={canEditGuards} />}
      {statusGuard && <StatusModal guard={statusGuard} isOpen={showStatusModal} onClose={() => setShowStatusModal(false)} onConfirm={handleUpdateStatus} />}
      <CreateGuardModal isOpen={showCreateModal} onClose={() => setShowCreateModal(false)} onSuccess={() => { loadGuards('first'); loadCount(); }} existingGuardIds={allGuards.map(g => g.guardId || '')} />
      <GuardDocumentsModal isOpen={showDocumentModal} onClose={() => setShowDocumentModal(false)} guard={documentGuard} />

      {historyGuard && (
        <DeploymentHistoryModal
          isOpen={showHistoryModal}
          onClose={() => setShowHistoryModal(false)}
          guard={historyGuard}
          companyId={companyId}
          onUpdate={() => {
            loadGuards('first');
          }}
        />
      )}

      <Modal
        isOpen={showResetConfirmModal}
        onClose={() => !isResettingDeployment && setShowResetConfirmModal(false)}
        title="Reset Guard Deployment"
      >
        <div style={{ padding: theme.spacing[4] }}>
          <div style={{
            backgroundColor: theme.colors.accent.danger + '10',
            padding: theme.spacing[4],
            borderRadius: theme.borderRadius.md,
            marginBottom: theme.spacing[4],
            color: theme.colors.accent.danger,
            display: 'flex',
            gap: theme.spacing[3],
            alignItems: 'center'
          }}>
            <FiAlertCircle size={24} />
            <div>
              <strong>Warning: Reset Guard Deployment</strong>
              <p style={{ margin: 0, fontSize: theme.typography.fontSize.sm }}>
                This will remove <strong>{resetGuard?.name}'s</strong> current deployment and all historical records. This action cannot be undone.
              </p>
            </div>
          </div>
          <p style={{ marginBottom: theme.spacing[2], fontWeight: theme.typography.fontWeight.medium }}>
            To confirm, please type <strong>RESET</strong> below:
          </p>
          <Input
            value={resetConfirmInput}
            onChange={(e) => setResetConfirmInput(e.target.value)}
            placeholder="Type RESET here"
            dark
          />
          <div style={{ display: 'flex', gap: theme.spacing[3], marginTop: theme.spacing[6] }}>
            <Button
              variant="secondary"
              onClick={() => setShowResetConfirmModal(false)}
              disabled={isResettingDeployment}
              fullWidth
              dark
            >
              Cancel
            </Button>
            <Button
              variant="danger"
              onClick={handleClearGuardDeployment}
              disabled={resetConfirmInput !== 'RESET' || isResettingDeployment}
              loading={isResettingDeployment}
              fullWidth
            >
              Reset Deployment
            </Button>
          </div>
        </div>
      </Modal>

      <ScrollToTopButton show={showScrollTop} onClick={scrollToTop}><FiArrowUp size={24} /></ScrollToTopButton>
    </DashboardLayout>
  );
};
export default ManageGuardPage;
